import { SelectProps } from '@amzn/awsui-components-react-v3';

import PACTemplateHeaders from '../../resources/PACRFQRequestTemplateHeaders.json';
import { EMPTY_OPTION } from '../../util/SelectUtils';
import { DCDIVEST, DCSP, DCSP_LLE,IPS, PAC } from '../BusinessConstants';
import { ITemplateHeader } from '../LIVE/model';
import {
  RFQ_DCDIVEST_LINEITEM_CLASS,
  RFQ_DCSP_DEFAULT_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS,
  RFQ_IPS_DETAIL_LINEITEM_CLASS,
  RFQ_IPS_EAM_LINEITEM_CLASS,
  RFQ_IPS_NON_EAM_LINEITEM_CLASS,
  RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS,
  RFQ_LINEITEM_CLASS
} from '../RFQClassDefinitionsConstants';
import { DCSP_QUOTE_TEMPLATE_TYPES, IPS_QUOTE_TEMPLATE_TYPES } from '../RFXCommonStepsData';
import { DCDivestTemplateExcelData,DCDivestTemplateHeaders } from './RFQDCDivestLineItemConstants';
import { DCSPGlobalComissioningBidTemplateHeaders } from './RFQDCSPLineItemConstants';
import { IPSDetailTemplateHeaders } from './RFQIPSDetailLineItemConstants';
import { IPSEAMTemplateHeaders } from './RFQIPSEAMLineItemConstants';
import { IPSNonEAMTemplateHeaders } from './RFQIPSNonEAMLineItemConstants';
import {
  IPSSecurityGuardTemplateExcelData,
  IPSSecurityGuardTemplateHeaders} from './RFQIPSSecurityGuardLineItemConstants';

const delimiter = '_';
const quoteTemplateFilenameSuffix = 'quote_template.xlsx';

/**
 * Item Id related headers are no longer needed in PAC quote template.
 */
export const PACLineItemHeades: string[] = ['Item Id', 'Commodity Id', 'Parent Id'];

/**
 * Return quote template headers in map.
 * The keys are worksheet names (DCSP and IPS uses multiple sheets) and values are headers.
 */
export const getQuoteTemplateHeaders = (rfxLineItemClassId: string): Map<string, string[]> => {
  switch (rfxLineItemClassId) {
    case RFQ_DCDIVEST_LINEITEM_CLASS:
      return getSingleSheetQuoteTemplateHeaders(DCDivestTemplateHeaders.map((header) => header.label));
    case RFQ_LINEITEM_CLASS:
      return getSingleSheetQuoteTemplateHeaders(
        PACTemplateHeaders.map((column) => column.headerName).filter((header) => !PACLineItemHeades.includes(header))
      );
    case RFQ_IPS_EAM_LINEITEM_CLASS:
      return getSingleSheetQuoteTemplateHeaders(IPSEAMTemplateHeaders.map((header) => header.label));
    case RFQ_IPS_DETAIL_LINEITEM_CLASS:
      return getSingleSheetQuoteTemplateHeaders(IPSDetailTemplateHeaders.map((header) => header.label));
    case RFQ_IPS_NON_EAM_LINEITEM_CLASS:
      return getSingleSheetQuoteTemplateHeaders(IPSNonEAMTemplateHeaders.map((header) => header.label));
    case RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS:
      return getSingleSheetQuoteTemplateHeaders(IPSSecurityGuardTemplateHeaders.map((header) => header.label));
    case RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS:
    case RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS:
    case RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS:
    case RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS:
    case RFQ_DCSP_DEFAULT_LINEITEM_CLASS:
      return DCSPGlobalComissioningBidTemplateHeaders;
    default:
      return new Map([['Quotes', []]]);
  }
};

export const getSingleSheetQuoteTemplateHeaders = (headers: string[]): Map<string, string[]> => {
  return new Map([['Quotes', headers]]);
};

export const getQuoteTemplateData = (rfxLineItemClassId: string) => {
  switch (rfxLineItemClassId) {
    case RFQ_DCDIVEST_LINEITEM_CLASS:
      return DCDivestTemplateExcelData;
    case RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS:
      return IPSSecurityGuardTemplateExcelData;
    default:
      return [];
  }
};

export const getQuoteTemplateFilename = (rfxLineItemClassId: string): string => {
  switch (rfxLineItemClassId) {
    case RFQ_LINEITEM_CLASS:
      return PAC + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_DCDIVEST_LINEITEM_CLASS:
      return DCDIVEST + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_IPS_EAM_LINEITEM_CLASS:
      return IPS + delimiter + 'EAM' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_IPS_DETAIL_LINEITEM_CLASS:
      return IPS + delimiter + 'EAM_Detail' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_IPS_NON_EAM_LINEITEM_CLASS:
      return IPS + delimiter + 'NON_EAM' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS:
      return IPS + delimiter + 'Security_Guard' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_DCSP_DEFAULT_LINEITEM_CLASS:
      return DCSP + delimiter + 'Global_Bid' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS:
      return DCSP + delimiter + 'Global_Commissioning_Bid' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS:
      return DCSP + delimiter + 'Global_DE' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS:
      return DCSP + delimiter + 'Global_GC_Bid' + delimiter + quoteTemplateFilenameSuffix;
    case RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS:
      return DCSP + delimiter + 'Global_Controls_Bid' + delimiter + quoteTemplateFilenameSuffix;
    default:
      return quoteTemplateFilenameSuffix;
  }
};

/**
 * Checks if provided line item is one of DCSP template classes
 * @param rfxLineItemClassId
 * @returns 
 */
export function isLineItemDCSP(rfxLineItemClassId: string): boolean {
  return [
    RFQ_DCSP_DEFAULT_LINEITEM_CLASS,
    RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS,
    RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS,
    RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS,
    RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS
  ].includes(rfxLineItemClassId);
}

/**
 * Returns the appropriate IPS quote template type based on the RFX line item class ID.
 * @param rfxLineItemClassId - The RFX line item class ID.
 * @returns The corresponding IPS quote template type as a SelectProps.Option.
 */
export const getIPSQuoteTemplateType = (rfxLineItemClassId: string): SelectProps.Option => {
  switch (rfxLineItemClassId) {
    case RFQ_IPS_EAM_LINEITEM_CLASS:
      return IPS_QUOTE_TEMPLATE_TYPES[0];
    case RFQ_IPS_DETAIL_LINEITEM_CLASS:
      return IPS_QUOTE_TEMPLATE_TYPES[1];
    case RFQ_IPS_NON_EAM_LINEITEM_CLASS:
      return IPS_QUOTE_TEMPLATE_TYPES[2];
    case RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS:
      return IPS_QUOTE_TEMPLATE_TYPES[3];
    default:
      return IPS_QUOTE_TEMPLATE_TYPES[4];
  }
};

/**
 * Returns the appropriate DCSP quote template type based on the RFX line item class ID.
 * @param rfxLineItemClassId - The RFX line item class ID.
 * @returns The corresponding DCSP quote template type as a SelectProps.Option.
 */
export const getDCSPQuoteTemplateType = (rfxLineItemClassId: string): SelectProps.Option => {
  switch (rfxLineItemClassId) {
    case RFQ_DCSP_DEFAULT_LINEITEM_CLASS:
      return DCSP_QUOTE_TEMPLATE_TYPES[0];
    case RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS:
      return DCSP_QUOTE_TEMPLATE_TYPES[1];
    case RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS:
      return DCSP_QUOTE_TEMPLATE_TYPES[2];
    case RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS:
      return DCSP_QUOTE_TEMPLATE_TYPES[3];
    case RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS:
      return DCSP_QUOTE_TEMPLATE_TYPES[4];
    default:
      return DCSP_QUOTE_TEMPLATE_TYPES[0];
  }
};

/**
 * Returns the appropriate quote template type based on the business ID and RFX line item class ID.
 * @param businessId - The business ID.
 * @param rfxLineItemClassId - The RFX line item class ID.
 * @returns The corresponding quote template type as a SelectProps.Option.
 */
export const getQuoteTemplateType = (
  businessId: string,
  workflow: string,
  rfxLineItemClassId: string
): SelectProps.Option => {
  switch (businessId) {
    case IPS:
      return getIPSQuoteTemplateType(rfxLineItemClassId);
    case DCSP:
      if (workflow === DCSP_LLE) {
        return EMPTY_OPTION;
      }
      return getDCSPQuoteTemplateType(rfxLineItemClassId);
    default:
      break;
  }
  return EMPTY_OPTION;
};
